import {Portal} from '@angular/cdk/portal';
import {Injectable} from '@angular/core';
import {MatSidenav} from '@angular/material/sidenav';
import {BehaviorSubject, from, Subject} from 'rxjs';

export type SidenavPositionType = 'start' | 'end';
export type SidenavSizeType = 'normal' | 'large' | 'full';

@Injectable({
  providedIn: 'root'
})
export class SidenavService {
  /** The sidenav. */
  leftSidenav?: MatSidenav;
  rightSidenav?: MatSidenav;
  isPushAction: boolean = true;
  private leftSidenavContents: BehaviorSubject<Portal<any>[]> = new BehaviorSubject<Portal<any>[]>([]);
  private rightSidenavContents: BehaviorSubject<Portal<any>[]> = new BehaviorSubject<Portal<any>[]>([]);
  private leftSidenavPortal$ = new Subject<Portal<any> | null>();
  private rightSidenavPortal$ = new Subject<Portal<any> | null>();
  private size$: SidenavSizeType = 'normal';
  private position$: SidenavPositionType = 'end';

  get size() {
    return this.size$;
  }

  get position() {
    return this.position$;
  }

  /** Retrieves the current sidenav portal as an `Observable`. */
  get leftSidenavPortal() {
    return from(this.leftSidenavPortal$);
  }

  get rightSidenavPortal() {
    return from(this.rightSidenavPortal$);
  }

  /** Opens the sidenav with optionally a portal to be set. */
  open(portal?: Portal<any>, position: SidenavPositionType = 'end', size: SidenavSizeType = 'normal') {
    const sidenav = position === 'start' ? this.leftSidenav : this.rightSidenav;
    if (portal) {
      if (position === 'start') {
        this.leftSidenavPortal$.next(portal);
      } else {
        this.rightSidenavPortal$.next(portal);
      }
    }
    this.position$ = position;
    this.size$ = size;
    return sidenav?.open();
  }

  /** Toggles the sidenav. */
  toggle(position: SidenavPositionType = 'end') {
    const sidenav = position === 'start' ? this.leftSidenav : this.rightSidenav;
    return sidenav?.toggle();
  }

  /** Closes the sidenav. */
  close(position: SidenavPositionType = 'end') {
    const sidenav = position === 'start' ? this.leftSidenav : this.rightSidenav;
    return sidenav?.close();
  }

  pushContent(portal: Portal<any>, position: SidenavPositionType = 'end', size: SidenavSizeType = 'normal', data?: any) {
    this.isPushAction = true;
    this.position$ = position;
    this.size$ = size;
    const sidenav = position === 'start' ? this.leftSidenav : this.rightSidenav;
    const sidenavContents = position === 'start' ? this.leftSidenavContents : this.rightSidenavContents;
    sidenavContents.next([...sidenavContents.value, portal]);
    this.setPortal(portal, position);
    sidenav?.open();
  }

  popContent() {
    this.isPushAction = false;
    const sidenav = this.position$ === 'start' ? this.leftSidenav : this.rightSidenav;
    const sidenavContents = this.position$ === 'start' ? this.leftSidenavContents : this.rightSidenavContents;
    const currentContents = sidenavContents.value;
    if (currentContents.length > 1) {
      const newContents = currentContents.slice(0, -1);
      sidenavContents.next(newContents);
      this.setPortal(newContents[newContents.length - 1], this.position$);
    } else {
      this.clearPortal(this.position$);
      sidenav?.close();
    }
  }

  clearContents(position: SidenavPositionType = 'end') {
    this.isPushAction = false;
    const sidenavContents = position === 'start' ? this.leftSidenavContents : this.rightSidenavContents;
    const sidenav = this.position$ === 'start' ? this.leftSidenav : this.rightSidenav;
    sidenavContents.next([]);
    this.clearPortal(position);
    sidenav?.close();
  }

  private setPortal(portal: Portal<any>, position: SidenavPositionType = 'end') {
    const sidenavPortal$ = position === 'start' ? this.leftSidenavPortal$ : this.rightSidenavPortal$;
    sidenavPortal$.next(portal);
  }

  private clearPortal(position: SidenavPositionType = 'end') {
    const sidenavPortal$ = position === 'start' ? this.leftSidenavPortal$ : this.rightSidenavPortal$;
    sidenavPortal$.next(null);
  }
}
